import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { NoGymSelected } from '@/components/NoGymSelected';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { facturacionApi } from '@/lib/facturacion';
import { gymApi } from '@/lib/api';
import {
  FacturacionCompany,
  FacturacionDocument,
  FacturacionEstablishment,
  FacturacionPointOfSale,
  FacturacionProviderConfig,
  FacturacionUsage,
  Membership,
  Sale,
} from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { reportError, setPageArea } from '@/lib/sentry';
import { Loader2, Receipt, Download, FileJson, RefreshCw, CheckCircle2, AlertTriangle, Building2, Store, BadgeDollarSign } from 'lucide-react';

const statusBadge = (status: string) => {
  switch (status) {
    case 'PROCESSED':
      return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">Procesado</Badge>;
    case 'DRAFT':
      return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">Borrador</Badge>;
    case 'REJECTED':
      return <Badge className="bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300">Rechazado</Badge>;
    case 'FAILED':
      return <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300">Fallido</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const fmtMoney = (n?: number | null) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-SV', { dateStyle: 'short', timeStyle: 'short' });
};

export default function FacturacionPage() {
  const { gym } = useAuth();
  const { toast } = useToast();

  const [provider, setProvider] = useState<FacturacionProviderConfig | null>(null);
  const [company, setCompany] = useState<FacturacionCompany | null>(null);
  const [establishments, setEstablishments] = useState<FacturacionEstablishment[]>([]);
  const [pointsOfSale, setPointsOfSale] = useState<FacturacionPointOfSale[]>([]);
  const [documents, setDocuments] = useState<FacturacionDocument[]>([]);
  const [usage, setUsage] = useState<FacturacionUsage[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [emittingId, setEmittingId] = useState<string | null>(null);

  // Formularios
  const [companyForm, setCompanyForm] = useState({
    legal_name: '',
    trade_name: '',
    tax_identity_number: '',
    tax_registration_number: '',
    economic_activity_code: '',
    taxpayer_type: 'SMALL',
  });
  const [estabForm, setEstabForm] = useState({
    official_code: '',
    establishment_type: 'HEADQUARTERS',
    department_code: '',
    district_code: '',
    address: '',
    email: '',
    phone: '',
  });
  const [posForm, setPosForm] = useState({ official_code: '', name: '' });

  useEffect(() => {
    setPageArea('revenue');
  }, []);

  const fetchConfig = async () => {
    if (!gym?.gym_id) return;
    try {
      await facturacionApi.ensureProvider(gym.gym_id);
      const res = await facturacionApi.getConfig(gym.gym_id);
      setProvider(res.provider);
      setCompany(res.company);
      setEstablishments(res.establishments);
      setPointsOfSale(res.pointsOfSale);
      if (res.company) {
        setCompanyForm({
          legal_name: res.company.legal_name || '',
          trade_name: res.company.trade_name || '',
          tax_identity_number: res.company.tax_identity_number || '',
          tax_registration_number: res.company.tax_registration_number || '',
          economic_activity_code: res.company.economic_activity_code || '',
          taxpayer_type: res.company.taxpayer_type || 'SMALL',
        });
      }
    } catch (error) {
      reportError(error, { operation: 'facturacion_config', area: 'revenue', extra: { gym_id: gym.gym_id } });
      toast({ title: 'Error', description: 'No se pudo cargar la configuración.', variant: 'destructive' });
    }
  };

  const fetchDocuments = async () => {
    if (!gym?.gym_id) return;
    try {
      const res = await facturacionApi.listDocuments(gym.gym_id);
      setDocuments(res.documents);
    } catch (error) {
      reportError(error, { operation: 'facturacion_documents', area: 'revenue', extra: { gym_id: gym.gym_id } });
    }
  };

  const fetchUsage = async () => {
    if (!gym?.gym_id) return;
    try {
      const res = await facturacionApi.getUsage(gym.gym_id);
      setUsage(res.usage || []);
    } catch (error) {
      reportError(error, { operation: 'facturacion_usage', area: 'revenue', extra: { gym_id: gym.gym_id } });
    }
  };

  const fetchSources = async () => {
    if (!gym?.gym_id) return;
    try {
      const [salesRes, membershipsRes] = await Promise.all([
        gymApi.listSales(gym.gym_id),
        gymApi.listMemberships(null, gym.gym_id),
      ]);
      setSales((salesRes.sales || []).slice(0, 30));
      setMemberships((membershipsRes.memberships || []).slice(0, 30));
    } catch (error) {
      reportError(error, { operation: 'facturacion_sources', area: 'revenue', extra: { gym_id: gym.gym_id } });
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchDocuments(), fetchUsage(), fetchSources()]);
      setLoading(false);
    };
    load();
  }, [gym?.gym_id]);

  const refreshAll = async () => {
    await Promise.all([fetchConfig(), fetchDocuments(), fetchUsage()]);
  };

  const handleRegisterCompany = async () => {
    if (!gym?.gym_id) return;
    setSubmitting(true);
    try {
      const res = await facturacionApi.registerCompany(gym.gym_id, companyForm);
      if (res?.success) {
        toast({ title: 'Empresa registrada', description: res.already_exists ? 'Ya existía en Lumen.' : 'Registrada correctamente.' });
        await fetchConfig();
      } else {
        toast({ title: 'Error', description: res?.error || res?.message || 'No se pudo registrar la empresa.', variant: 'destructive' });
      }
    } catch (error) {
      reportError(error, { operation: 'facturacion_register_company', area: 'revenue', extra: { gym_id: gym.gym_id } });
      toast({ title: 'Error', description: 'No se pudo registrar la empresa.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterEstablishment = async () => {
    if (!gym?.gym_id || !company) return;
    setSubmitting(true);
    try {
      const res = await facturacionApi.registerEstablishment(gym.gym_id, {
        tax_registration_number: company.tax_registration_number,
        ...estabForm,
      });
      if (res?.success) {
        toast({ title: 'Establecimiento registrado', description: res.already_exists ? 'Ya existía en Lumen.' : 'Registrado correctamente.' });
        setEstabForm({ official_code: '', establishment_type: 'HEADQUARTERS', department_code: '', district_code: '', address: '', email: '', phone: '' });
        await fetchConfig();
      } else {
        toast({ title: 'Error', description: res?.error || res?.message || 'No se pudo registrar el establecimiento.', variant: 'destructive' });
      }
    } catch (error) {
      reportError(error, { operation: 'facturacion_register_estab', area: 'revenue', extra: { gym_id: gym.gym_id } });
      toast({ title: 'Error', description: 'No se pudo registrar el establecimiento.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterPointOfSale = async () => {
    if (!gym?.gym_id || !company || !establishments.length) return;
    setSubmitting(true);
    try {
      const res = await facturacionApi.registerPointOfSale(gym.gym_id, {
        establishment_code: establishments[0].official_code,
        tax_registration_number: company.tax_registration_number,
        ...posForm,
      });
      if (res?.success) {
        toast({ title: 'Punto de venta registrado', description: res.already_exists ? 'Ya existía en Lumen.' : 'Registrado correctamente.' });
        setPosForm({ official_code: '', name: '' });
        await fetchConfig();
      } else {
        toast({ title: 'Error', description: res?.error || res?.message || 'No se pudo registrar el punto de venta.', variant: 'destructive' });
      }
    } catch (error) {
      reportError(error, { operation: 'facturacion_register_pos', area: 'revenue', extra: { gym_id: gym.gym_id } });
      toast({ title: 'Error', description: 'No se pudo registrar el punto de venta.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnvironmentChange = async (env: 'SANDBOX' | 'PRODUCTION') => {
    if (!gym?.gym_id) return;
    try {
      await facturacionApi.setEnvironment(gym.gym_id, env);
      setProvider((p) => (p ? { ...p, environment: env } : p));
      toast({ title: 'Entorno actualizado', description: env === 'SANDBOX' ? 'Modo sandbox (sin impacto fiscal).' : 'Modo producción.' });
    } catch (error) {
      reportError(error, { operation: 'facturacion_env', area: 'revenue', extra: { gym_id: gym.gym_id } });
      toast({ title: 'Error', description: 'No se pudo cambiar el entorno.', variant: 'destructive' });
    }
  };

  const handleEmit = async (sourceType: 'sale' | 'membership', sourceId: string) => {
    if (!gym?.gym_id) return;
    setEmittingId(`${sourceType}-${sourceId}`);
    try {
      const res = await facturacionApi.emitDocument(gym.gym_id, sourceType, sourceId);
      const status = res?.outcome?.status || res?.document?.status;
      if (res?.success) {
        toast({
          title: status === 'DRAFT' ? 'Factura en borrador' : 'Factura emitida',
          description: `Estado: ${status}. Control: ${res?.document?.control_number || '—'}`,
        });
      } else {
        toast({
          title: 'No se pudo emitir',
          description: `${status || 'Error'}: ${res?.outcome?.message || res?.document?.error_message || res?.error || 'Revisa los datos.'}`,
          variant: 'destructive',
        });
      }
      await Promise.all([fetchDocuments(), fetchUsage()]);
    } catch (error) {
      reportError(error, { operation: 'facturacion_emit', area: 'revenue', extra: { gym_id: gym.gym_id, source_type: sourceType, source_id: sourceId } });
      toast({ title: 'Error', description: 'No se pudo emitir la factura.', variant: 'destructive' });
    } finally {
      setEmittingId(null);
    }
  };

  const handleDownload = async (doc: FacturacionDocument, kind: 'pdf' | 'json') => {
    if (!gym?.gym_id || !doc.generation_code) return;
    try {
      const res = kind === 'pdf'
        ? await facturacionApi.downloadPdf(gym.gym_id, doc.generation_code)
        : await facturacionApi.downloadJson(gym.gym_id, doc.generation_code);
      if (res?.url) {
        window.open(res.url, '_blank');
      } else {
        toast({ title: 'Error', description: res?.error || 'No se pudo obtener el archivo.', variant: 'destructive' });
      }
    } catch (error) {
      reportError(error, { operation: 'facturacion_download', area: 'revenue', extra: { gym_id: gym.gym_id, generation_code: doc.generation_code, kind } });
      toast({ title: 'Error', description: 'No se pudo obtener el archivo.', variant: 'destructive' });
    }
  };

  if (!gym) return <DashboardLayout title="Facturación electrónica"><NoGymSelected /></DashboardLayout>;

  const emisorCompleto = company && establishments.length > 0 && pointsOfSale.length > 0;

  return (
    <DashboardLayout title="Facturación electrónica">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <BadgeDollarSign className="h-4 w-4" />
            {emisorCompleto ? (
              <span className="flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> Emisor configurado
              </span>
            ) : (
              <span className="flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" /> Completa la configuración del emisor
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={refreshAll}>
            <RefreshCw className="mr-2 h-4 w-4" /> Actualizar
          </Button>
        </div>

        <Tabs defaultValue="config">
          <TabsList>
            <TabsTrigger value="config">Configuración</TabsTrigger>
            <TabsTrigger value="emitir">Emitir</TabsTrigger>
            <TabsTrigger value="documentos">Documentos</TabsTrigger>
            <TabsTrigger value="consumo">Consumo</TabsTrigger>
          </TabsList>

          {/* ---------- CONFIGURACIÓN ---------- */}
          <TabsContent value="config" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Entorno</CardTitle>
                <CardDescription>Sandbox no tiene impacto fiscal; producción transmite a Hacienda.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-xs">
                  <Label className="text-xs uppercase text-muted-foreground">Entorno</Label>
                  <Select value={provider?.environment ?? 'SANDBOX'} onValueChange={handleEnvironmentChange}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SANDBOX">Sandbox (pruebas)</SelectItem>
                      <SelectItem value="PRODUCTION">Producción</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Empresa */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> Empresa emisora</CardTitle>
                <CardDescription>Datos fiscales ante Hacienda (NIT, NRC, actividad económica).</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div><Label>Razón social</Label><Input className="mt-1" value={companyForm.legal_name} onChange={(e) => setCompanyForm({ ...companyForm, legal_name: e.target.value })} /></div>
                <div><Label>Nombre comercial</Label><Input className="mt-1" value={companyForm.trade_name} onChange={(e) => setCompanyForm({ ...companyForm, trade_name: e.target.value })} /></div>
                <div><Label>NIT</Label><Input className="mt-1" placeholder="0614-090590-123-4" value={companyForm.tax_identity_number} onChange={(e) => setCompanyForm({ ...companyForm, tax_identity_number: e.target.value })} /></div>
                <div><Label>NRC</Label><Input className="mt-1" placeholder="123456-7" value={companyForm.tax_registration_number} onChange={(e) => setCompanyForm({ ...companyForm, tax_registration_number: e.target.value })} /></div>
                <div><Label>Código de actividad económica</Label><Input className="mt-1" placeholder="46900" value={companyForm.economic_activity_code} onChange={(e) => setCompanyForm({ ...companyForm, economic_activity_code: e.target.value })} /></div>
                <div>
                  <Label>Tipo de contribuyente</Label>
                  <Select value={companyForm.taxpayer_type} onValueChange={(v) => setCompanyForm({ ...companyForm, taxpayer_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SMALL">Pequeño</SelectItem>
                      <SelectItem value="MEDIUM">Mediano</SelectItem>
                      <SelectItem value="LARGE">Grande</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Button onClick={handleRegisterCompany} disabled={submitting}>
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {company ? 'Actualizar empresa' : 'Registrar empresa'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Establecimiento */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Store className="h-5 w-5" /> Establecimiento</CardTitle>
                <CardDescription>El código oficial (ej. M001) lo asigna Hacienda en factura.gob.sv.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div><Label>Código oficial</Label><Input className="mt-1" placeholder="M001" value={estabForm.official_code} onChange={(e) => setEstabForm({ ...estabForm, official_code: e.target.value })} /></div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={estabForm.establishment_type} onValueChange={(v) => setEstabForm({ ...estabForm, establishment_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HEADQUARTERS">Casa matriz</SelectItem>
                      <SelectItem value="BRANCH">Sucursal</SelectItem>
                      <SelectItem value="WAREHOUSE">Bodega</SelectItem>
                      <SelectItem value="PATIO">Patio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Departamento (código)</Label><Input className="mt-1" placeholder="06" value={estabForm.department_code} onChange={(e) => setEstabForm({ ...estabForm, department_code: e.target.value })} /></div>
                <div><Label>Distrito (código)</Label><Input className="mt-1" placeholder="01" value={estabForm.district_code} onChange={(e) => setEstabForm({ ...estabForm, district_code: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Dirección</Label><Input className="mt-1" value={estabForm.address} onChange={(e) => setEstabForm({ ...estabForm, address: e.target.value })} /></div>
                <div><Label>Email</Label><Input className="mt-1" value={estabForm.email} onChange={(e) => setEstabForm({ ...estabForm, email: e.target.value })} /></div>
                <div><Label>Teléfono</Label><Input className="mt-1" value={estabForm.phone} onChange={(e) => setEstabForm({ ...estabForm, phone: e.target.value })} /></div>
                <div className="sm:col-span-2">
                  <Button onClick={handleRegisterEstablishment} disabled={submitting || !company}>
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Registrar establecimiento
                  </Button>
                </div>
                {establishments.length > 0 && (
                  <div className="sm:col-span-2 text-sm text-muted-foreground">
                    Registrados: {establishments.map((e) => e.official_code).join(', ')}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Punto de venta */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Punto de venta</CardTitle>
                <CardDescription>El código oficial (ej. P001) lo asigna Hacienda.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div><Label>Código oficial</Label><Input className="mt-1" placeholder="P001" value={posForm.official_code} onChange={(e) => setPosForm({ ...posForm, official_code: e.target.value })} /></div>
                <div><Label>Nombre (opcional)</Label><Input className="mt-1" placeholder="Caja principal" value={posForm.name} onChange={(e) => setPosForm({ ...posForm, name: e.target.value })} /></div>
                <div className="sm:col-span-2">
                  <Button onClick={handleRegisterPointOfSale} disabled={submitting || !company || !establishments.length}>
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Registrar punto de venta
                  </Button>
                </div>
                {pointsOfSale.length > 0 && (
                  <div className="sm:col-span-2 text-sm text-muted-foreground">
                    Registrados: {pointsOfSale.map((p) => p.official_code).join(', ')}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- EMITIR ---------- */}
          <TabsContent value="emitir" className="space-y-4">
            {!emisorCompleto && (
              <Card className="border-amber-300 bg-amber-50 dark:bg-amber-500/10">
                <CardContent className="pt-4 text-sm">
                  Completa empresa, establecimiento y punto de venta en la pestaña Configuración antes de emitir.
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Ventas (POS)</CardTitle>
                <CardDescription>Emitir factura de consumidor final desde una venta ya registrada.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No hay ventas registradas.</TableCell></TableRow>
                    )}
                    {sales.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="text-xs">{fmtDate(s.created_at)}</TableCell>
                        <TableCell className="text-sm">{s.client_name || 'Consumidor final'}</TableCell>
                        <TableCell className="text-xs">{s.payment_method}</TableCell>
                        <TableCell className="text-right font-medium">{fmtMoney(s.total_amount)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" disabled={!emisorCompleto || emittingId === `sale-${s.id}`} onClick={() => handleEmit('sale', s.id)}>
                            {emittingId === `sale-${s.id}` ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Receipt className="mr-1 h-3 w-3" />}
                            Emitir CCF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Membresías</CardTitle>
                <CardDescription>Emitir factura por el pago de una membresía.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberships.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No hay membresías registradas.</TableCell></TableRow>
                    )}
                    {memberships.map((m) => (
                      <TableRow key={m.membership_id}>
                        <TableCell className="text-xs">{fmtDate(m.start_date)}</TableCell>
                        <TableCell className="text-sm">{m.client_id ? 'Socio' : '—'}</TableCell>
                        <TableCell className="text-sm">{m.type}</TableCell>
                        <TableCell className="text-right font-medium">{fmtMoney(m.price)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" disabled={!emisorCompleto || emittingId === `membership-${m.membership_id}`} onClick={() => handleEmit('membership', m.membership_id)}>
                            {emittingId === `membership-${m.membership_id}` ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Receipt className="mr-1 h-3 w-3" />}
                            Emitir CCF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- DOCUMENTOS ---------- */}
          <TabsContent value="documentos">
            <Card>
              <CardHeader>
                <CardTitle>Documentos emitidos</CardTitle>
                <CardDescription>Historial de DTE con estado y descarga de PDF/JSON.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Control</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Aún no hay documentos.</TableCell></TableRow>
                    )}
                    {documents.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell className="text-xs">{fmtDate(d.created_at)}</TableCell>
                        <TableCell className="text-xs font-mono">{d.control_number || '—'}</TableCell>
                        <TableCell className="text-sm">{d.client_name || 'Consumidor final'}</TableCell>
                        <TableCell>{statusBadge(d.status)}</TableCell>
                        <TableCell className="text-right font-medium">{fmtMoney(d.total_amount)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" title="Descargar PDF" disabled={!d.generation_code} onClick={() => handleDownload(d, 'pdf')}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" title="Descargar JSON" disabled={!d.generation_code} onClick={() => handleDownload(d, 'json')}>
                              <FileJson className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- CONSUMO ---------- */}
          <TabsContent value="consumo" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Emitidos (mes)</CardTitle></CardHeader>
                <CardContent className="text-2xl font-bold">{usage[0]?.total_count ?? 0}</CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Procesados (mes)</CardTitle></CardHeader>
                <CardContent className="text-2xl font-bold">{usage[0]?.processed_count ?? 0}</CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Monto procesado (mes)</CardTitle></CardHeader>
                <CardContent className="text-2xl font-bold">{fmtMoney(usage[0]?.processed_amount)}</CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Historial mensual</CardTitle>
                <CardDescription>Solo los documentos procesados (PROCESSED) cuentan como DTE transmitido.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mes</TableHead>
                      <TableHead className="text-right">Emitidos</TableHead>
                      <TableHead className="text-right">Procesados</TableHead>
                      <TableHead className="text-right">Monto procesado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usage.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Sin datos todavía.</TableCell></TableRow>
                    )}
                    {usage.map((u) => (
                      <TableRow key={u.period}>
                        <TableCell className="text-sm">{new Date(u.period).toLocaleDateString('es-SV', { month: 'long', year: 'numeric' })}</TableCell>
                        <TableCell className="text-right">{u.total_count}</TableCell>
                        <TableCell className="text-right">{u.processed_count}</TableCell>
                        <TableCell className="text-right font-medium">{fmtMoney(u.processed_amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
